/**
 * Client-side image downscale before upload. Aspect-ratio preserved
 * (never crops), never upscales, uses high-quality canvas resampling.
 *
 * Rationale: Anthropic and Google Gemini bill vision input by tile, and
 * a 4 MB phone screenshot costs 3–4× a properly-resized one for zero
 * quality gain on UI/screenshot understanding. Capping the longest side
 * at 1568 px matches Anthropic's recommended max tile size — anything
 * larger is downsampled server-side anyway.
 *
 * Pass-through cases (returned unchanged):
 *   - SVG: vector, no benefit to rasterizing.
 *   - GIF: animation would be lost on canvas re-encode.
 *   - Non-images: ignored — caller passes them straight through.
 *   - Images already within MAX_LONGEST_SIDE: no re-encode, preserves
 *     original bytes exactly.
 *
 * Image stats (original size + dimensions, compressed size + dimensions)
 * are tracked in a module-local WeakMap so callers don't have to thread
 * a new return shape through every component. Read via `getImageStats`.
 */

const MAX_LONGEST_SIDE = 1568;
const JPEG_QUALITY = 0.92;
const PASSTHROUGH_TYPES = new Set(["image/svg+xml", "image/gif"]);

function shouldResize(type: string) {
  return type.startsWith("image/") && !PASSTHROUGH_TYPES.has(type);
}

export interface ImageStats {
  originalSize: number;
  originalWidth: number;
  originalHeight: number;
  compressedSize: number;
  compressedWidth: number;
  compressedHeight: number;
  /** "resize" = bytes shrank via canvas; "passthrough" = under threshold
   *  or unsupported type, original bytes returned unchanged. */
  kind: "resize" | "passthrough";
}

// Keyed off the File reference returned from resizeImageFile. Survives as
// long as the File survives — the upload path holds the reference long
// enough for the originalSize to be read out into FormData.
const statsByFile = new WeakMap<File, ImageStats>();

export function getImageStats(file: File): ImageStats | undefined {
  return statsByFile.get(file);
}

async function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to HTMLImageElement loader
    }
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image decode failed"));
    };
    img.src = url;
  });
}

function pickOutputType(inputType: string): string {
  // Keep PNG as PNG so any alpha channel survives lossless. Anything else
  // (JPEG, WebP, AVIF, HEIC) re-encodes to JPEG at high quality — smaller
  // and universally accepted by vision models.
  return inputType === "image/png" ? "image/png" : "image/jpeg";
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function logResize(stats: ImageStats, name: string) {
  const saved = Math.max(0, stats.originalSize - stats.compressedSize);
  const pct =
    stats.originalSize > 0
      ? Math.round((saved / stats.originalSize) * 100)
      : 0;
  const action = stats.kind === "resize" ? "resized" : "kept as-is";
  // eslint-disable-next-line no-console
  console.info(
    `[image-resize] ${name}: ${action} · ` +
      `${fmtBytes(stats.originalSize)} (${stats.originalWidth}×${stats.originalHeight}) → ` +
      `${fmtBytes(stats.compressedSize)} (${stats.compressedWidth}×${stats.compressedHeight}) · ` +
      `saved ${fmtBytes(saved)} (${pct}%)`,
  );
}

/**
 * Downscale a single image File if its longest side exceeds
 * MAX_LONGEST_SIDE. Returns the original File unchanged when:
 *   - it's not a raster image
 *   - it's already at or below the threshold
 *   - canvas/encode fails for any reason (graceful — never blocks upload)
 *
 * On any successful path, ImageStats are stored in the module WeakMap so
 * the upload layer can attach originalSize to the request.
 */
export async function resizeImageFile(file: File): Promise<File> {
  if (!shouldResize(file.type)) return file;

  try {
    const bitmap = await loadImageBitmap(file);
    const w = "width" in bitmap ? bitmap.width : (bitmap as HTMLImageElement).naturalWidth;
    const h = "height" in bitmap ? bitmap.height : (bitmap as HTMLImageElement).naturalHeight;
    if (!w || !h) return file;

    const longest = Math.max(w, h);
    if (longest <= MAX_LONGEST_SIDE) {
      // Already small enough — return original bytes untouched. No
      // re-encode means no quality loss whatsoever for in-budget images.
      if ("close" in bitmap) bitmap.close();
      const stats: ImageStats = {
        originalSize: file.size,
        originalWidth: w,
        originalHeight: h,
        compressedSize: file.size,
        compressedWidth: w,
        compressedHeight: h,
        kind: "passthrough",
      };
      statsByFile.set(file, stats);
      logResize(stats, file.name);
      return file;
    }

    const scale = MAX_LONGEST_SIDE / longest;
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      if ("close" in bitmap) bitmap.close();
      return file;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap as CanvasImageSource, 0, 0, targetW, targetH);
    if ("close" in bitmap) bitmap.close();

    const outType = pickOutputType(file.type);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outType, outType === "image/jpeg" ? JPEG_QUALITY : undefined),
    );
    if (!blob) return file;

    // If re-encoding produced a *bigger* file (rare — happens with PNGs
    // of mostly-uniform content), keep the original.
    if (blob.size >= file.size) {
      const stats: ImageStats = {
        originalSize: file.size,
        originalWidth: w,
        originalHeight: h,
        compressedSize: file.size,
        compressedWidth: w,
        compressedHeight: h,
        kind: "passthrough",
      };
      statsByFile.set(file, stats);
      logResize(stats, file.name);
      return file;
    }

    const ext = outType === "image/png" ? "png" : "jpg";
    const base = file.name.replace(/\.[^./\\]+$/, "");
    const newFile = new File([blob], `${base}.${ext}`, {
      type: outType,
      lastModified: file.lastModified,
    });
    const stats: ImageStats = {
      originalSize: file.size,
      originalWidth: w,
      originalHeight: h,
      compressedSize: blob.size,
      compressedWidth: targetW,
      compressedHeight: targetH,
      kind: "resize",
    };
    statsByFile.set(newFile, stats);
    logResize(stats, newFile.name);
    return newFile;
  } catch {
    return file;
  }
}

export async function resizeImageFiles(files: File[]): Promise<File[]> {
  return Promise.all(files.map(resizeImageFile));
}
